import { useMemo, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '@/services/auth/authService';
import { isFeatureEnabled, useFeatureValues } from '@/lib/moduleFeatures';
import {
  buildCommandRegistry,
  matchCommands,
  recentCommandsStore,
  type MatchInput,
  type MatchedCommand,
  type SlashCommand,
  type LocalizedText,
  isDynamicGroup,
} from '@/components/GlobalSearch/commands';
import { useContextCommands } from './useContextCommands';
import { useRelatedCommands } from './useRelatedCommands';
import { useRecentRecordCommands } from './useRecentRecordCommands';

/**
 * A command shown under the "Recent" group takes precedence over its normal group
 * while input is empty, so we never render the same command twice.
 */
export interface SlashCommandGroupedResult {
  /** Actions available on the record the current page is showing. Always rendered first. */
  actions: MatchedCommand[];
  /** "Show related" jumps from the record the current page is showing. */
  related: MatchedCommand[];
  /** Records the user has recently opened. */
  history: MatchedCommand[];
  /** Most-recently-used commands (localized, filtered by permission, capped at 5). */
  recent: MatchedCommand[];
  /** "Create X" commands (filtered). */
  create: MatchedCommand[];
  /** "Go to X" commands (filtered). */
  navigation: MatchedCommand[];
  /** Utility commands (filtered). */
  utilities: MatchedCommand[];
}

const MAX_RECENT_VISIBLE = 5;
const MAX_PER_GROUP = 8;
// History earns a taller section than the other groups: it is the whole point of the
// section, and the store only keeps 20 in the first place.
const MAX_HISTORY_VISIBLE = 12;

// Static commands are pure, so build them once at module load. Context actions cannot
// join them here — they depend on the current route and a server response, so they are
// merged in the hook below.
const staticRegistry: SlashCommand[] = buildCommandRegistry();

const permissionsSubscribe = (cb: () => void) => authService.subscribePermissions(cb);
const permissionsSnapshot = () => authService.getPermissions();

const recentSubscribe = (cb: () => void) => recentCommandsStore.subscribe(cb);
const recentSnapshot = () => recentCommandsStore.getRecent();

/** Check whether all required permissions are granted. Empty/undefined = no restriction. */
function hasAllPermissions(
  required: string | string[] | undefined,
  granted: readonly string[],
): boolean {
  if (!required) return true;
  const list = Array.isArray(required) ? required : [required];
  if (list.length === 0) return true;
  return list.every((p) => granted.includes(p));
}

/**
 * Resolve a LocalizedText bundle to a concrete string, preferring the i18n key but
 * falling back to the raw value if the key is missing.
 */
function resolveText(t: ReturnType<typeof useTranslation>['t'], text: LocalizedText | undefined): string {
  if (!text) return '';
  if (text.key) {
    const fallback = text.value ?? text.key;
    return t(text.key, fallback, resolveArgs(t, text)) as string;
  }
  return text.value ?? '';
}

/**
 * Resolve the interpolation args, looking up every `argKeys` entry through i18n first.
 * A nav command's view name is a `Menu:*` key, so without this the palette would show and
 * match the English label while the sidebar shows the translated one.
 */
function resolveArgs(
  t: ReturnType<typeof useTranslation>['t'],
  text: LocalizedText,
): Record<string, string> | undefined {
  if (!text.argKeys) return text.args;
  const resolved: Record<string, string> = { ...(text.args ?? {}) };
  for (const [name, key] of Object.entries(text.argKeys)) {
    resolved[name] = t(key, { defaultValue: text.args?.[name] ?? key }) as string;
  }
  return resolved;
}

/** Build a MatchInput tuple (command + localized strings + haystack) for a single command. */
function buildMatchInput(
  t: ReturnType<typeof useTranslation>['t'],
  command: SlashCommand,
): MatchInput {
  const localizedTitle = resolveText(t, command.title);
  const localizedSubtitle = resolveText(t, command.subtitle);

  // Optional comma-separated localized keyword list (e.g. Swedish "kund,kunder,klient").
  const localizedKeywords = command.localizedKeywordsKey
    ? (t(command.localizedKeywordsKey, '') as string)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  const haystack = [
    localizedTitle,
    localizedSubtitle,
    ...(command.keywords ?? []),
    ...localizedKeywords,
    command.id,
  ];

  return { command, localizedTitle, localizedSubtitle, haystack };
}

export interface UseSlashCommandsResult {
  /** True when the user input should be interpreted as a command (starts with "/"). */
  isSlashMode: boolean;
  /** Flat list of visible commands in render order — used for keyboard navigation. */
  flat: MatchedCommand[];
  /** Same commands split into groups (for sectioned rendering). Group order is fixed. */
  grouped: SlashCommandGroupedResult;
  /** Start indexes of each non-empty group within `flat` — used for Tab navigation. */
  groupStartIndices: number[];
}

/**
 * Build the filtered, localized, scored list of slash commands for the given input.
 *
 * @param rawInput The raw dialog input, e.g. "/cust" or "c" (anything not starting with "/" returns empty results).
 */
export function useSlashCommands(rawInput: string): UseSlashCommandsResult {
  const isSlashMode = rawInput.startsWith('/');
  const query = isSlashMode ? rawInput.slice(1).trim() : '';

  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const contextCommands = useContextCommands();
  const relatedCommands = useRelatedCommands();
  const historyCommands = useRecentRecordCommands();
  const registry = useMemo(
    () => [...contextCommands, ...relatedCommands, ...historyCommands, ...staticRegistry],
    [contextCommands, relatedCommands, historyCommands],
  );

  const grantedPermissions = useSyncExternalStore(permissionsSubscribe, permissionsSnapshot);
  const featureValues = useFeatureValues();
  const recentIds = useSyncExternalStore(recentSubscribe, recentSnapshot);

  // Step 1: permission + feature filter. Cheap; re-runs only when either changes.
  const permitted = useMemo(
    () => registry.filter((cmd) =>
      hasAllPermissions(cmd.permission, grantedPermissions)
      && isFeatureEnabled(featureValues, cmd.feature)),
    [registry, grantedPermissions, featureValues],
  );

  // Step 2: localize. Re-run on language change so titles/keywords track the active locale.
  // Intentional dependency on `language` even though `t` closes over it — ensures recompute.
  const localized = useMemo(
    () => permitted.map((cmd) => buildMatchInput(t, cmd)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permitted, language],
  );

  // Step 3: match + sort.
  const matched = useMemo(() => matchCommands(localized, query), [localized, query]);

  return useMemo(() => {
    if (!isSlashMode) {
      return {
        isSlashMode: false,
        flat: [],
        grouped: { actions: [], related: [], history: [], recent: [], create: [], navigation: [], utilities: [] },
        groupStartIndices: [],
      };
    }

    const byId = new Map<string, MatchedCommand>();
    for (const m of matched) byId.set(m.command.id, m);

    // Recent: keep the store's order. Only surface entries that still exist AND
    // either the query is empty, or the command would also match the query.
    const recent: MatchedCommand[] = [];
    for (const id of recentIds) {
      const m = byId.get(id);
      if (!m) continue;
      // Defensive: storage written before dynamic commands were excluded still holds their
      // ids. Without this they keep surfacing under "Recent" — and, via the dedup below,
      // keep disappearing from the section they belong to — until they age out.
      if (isDynamicGroup(m.command.group)) continue;
      recent.push(m);
      if (recent.length >= MAX_RECENT_VISIBLE) break;
    }

    const recentIdSet = new Set(recent.map((m) => m.command.id));

    const actions: MatchedCommand[] = [];
    const related: MatchedCommand[] = [];
    const history: MatchedCommand[] = [];
    const create: MatchedCommand[] = [];
    const navigation: MatchedCommand[] = [];
    const utilities: MatchedCommand[] = [];

    for (const m of matched) {
      if (recentIdSet.has(m.command.id)) continue; // avoid duplicates
      switch (m.command.group) {
        case 'actions':
          // Uncapped: an entity's action set is small and already scoped to one record,
          // so truncating it would hide exactly what the group exists to show.
          actions.push(m);
          break;
        case 'related':
          // Uncapped for the same reason: the set is the record's declared related views.
          related.push(m);
          break;
        case 'history':
          if (history.length < MAX_HISTORY_VISIBLE) history.push(m);
          break;
        case 'create':
          if (create.length < MAX_PER_GROUP) create.push(m);
          break;
        case 'navigation':
          if (navigation.length < MAX_PER_GROUP) navigation.push(m);
          break;
        case 'utilities':
          if (utilities.length < MAX_PER_GROUP) utilities.push(m);
          break;
      }
    }

    // Actions first — they are the most specific thing the palette can offer — then the
    // record's related jumps, before the generic groups.
    const flat = [...actions, ...related, ...history, ...recent, ...create, ...navigation, ...utilities];

    // Compute group start indices (only for non-empty groups, in render order).
    const groupStartIndices: number[] = [];
    let cursor = 0;
    for (const group of [actions, related, history, recent, create, navigation, utilities]) {
      if (group.length > 0) {
        groupStartIndices.push(cursor);
        cursor += group.length;
      }
    }

    return {
      isSlashMode: true,
      flat,
      grouped: { actions, related, history, recent, create, navigation, utilities },
      groupStartIndices,
    };
  }, [isSlashMode, matched, recentIds]);
}
